import { onRolesMessage } from './ugc';
import './styles.css';
import { render } from 'preact';
import type { ServerMessage } from '@cratered/shared';
import { setupDiscord } from './discord';
import { Net, wsUrl } from './net';
import { startPresence } from './presence';
import {
  discord, hostGrant, joinedInProgress, layoutMode, lobby, match, net, netStatus, participants, profile, results, screen, self,
  showToast, speaking,
} from './store';
import { App } from './ui/App';
import { installAudioListeners } from './game/audio';
import { loadMusic, music } from './game/music';

const root = document.getElementById('app')!;
installAudioListeners();
// Music waits for the first click, tap or key press, pauses while the page is hidden, and only exists if tracks.json lists a track.
// A touch only counts as a gesture to the browser when the finger lifts, so those events unlock it too.
for (const type of ['pointerdown', 'pointerup', 'click', 'keydown'] as const) document.addEventListener(type, () => music.unlock(), { capture: true });
document.addEventListener('visibilitychange', () => music.setHidden(document.visibilityState === 'hidden'));
void loadMusic();

/** Notes when, on this client's clock, an open vote was received, so its countdown can run from the server's remaining time. */
function stampVote(lobby: { vote?: { receivedAt?: number } }): void {
  if (lobby.vote) lobby.vote.receivedAt = Date.now();
}

function handle(msg: ServerMessage): void {
  switch (msg.t) {
    case 'welcome': {
      self.value = msg.you;
      profile.value = msg.profile;
      stampVote(msg.lobby);
      lobby.value = msg.lobby;
      const m = msg.lobby.phase === 'match' ? msg.lobby.match : undefined;
      if (m) {
        // Reconnecting with the match still on screen: carry on. A fresh page has none of the state.
        const resumed = screen.value === 'match' && match.value?.seed === m.seed;
        if (resumed) {
          match.value = m;
          if (m.hostId === msg.you.id) hostGrant.value = m;
        } else {
          joinedInProgress.value = true;
          if (m.hostId === msg.you.id) {
            // Listed as host but just (re)loaded: let a client that still has the match carry it on.
            net.value?.send({ t: 'resignHost' });
            match.value = { ...m, hostId: '' };
          } else {
            match.value = m;
          }
        }
        screen.value = 'match';
      } else if (screen.value === 'match' || screen.value === 'results') {
        screen.value = 'lobby';
      }
      break;
    }
    case 'lobby':
      stampVote(msg.lobby);
      lobby.value = msg.lobby;
      if (msg.lobby.phase === 'lobby' && (screen.value === 'match' || screen.value === 'results')) {
        match.value = null;
        results.value = null;
        hostGrant.value = null;
        screen.value = 'lobby';
      }
      break;
    case 'becomeHost':
      match.value = msg.match;
      hostGrant.value = msg.match;
      break;
    case 'profile':
      profile.value = msg.profile;
      break;
    case 'matchStart':
      match.value = msg.match;
      results.value = null;
      hostGrant.value = null;
      joinedInProgress.value = false;
      screen.value = 'match';
      break;
    case 'results':
      results.value = { rewards: msg.rewards, outcomes: msg.outcomes };
      screen.value = 'results';
      break;
    case 'error':
      showToast(msg.message, 'error');
      break;
    default:
      break;
  }
}

async function boot(): Promise<void> {
  render(<App />, root);
  try {
    const bridge = await setupDiscord();
    discord.value = bridge;
    bridge.participants().then((p) => (participants.value = p)).catch(() => {});
    bridge.onParticipants((p) => (participants.value = p));
    bridge.onLayoutMode((m) => (layoutMode.value = m));
    bridge.onSpeaking((id, on) => {
      const s = new Set(speaking.value);
      if (on) s.add(id);
      else s.delete(id);
      speaking.value = s;
    });

    const n = new Net(wsUrl(bridge.apiBase), () => ({
      instanceId: bridge.instanceId,
      auth: bridge.accessToken
        ? { kind: 'discord', accessToken: bridge.accessToken }
        : { kind: 'dev', userId: bridge.user.id, name: bridge.user.global_name || bridge.user.username },
    }));
    n.on(onRolesMessage);
    n.on(handle);
    n.onStatus((s) => {
      netStatus.value = s;
      if (s === 'offline') showToast('Connection lost. Reconnecting…', 'error');
    });
    n.connect();
    net.value = n;
    startPresence();
  } catch (err) {
    console.error('[boot]', err);
    showToast(err instanceof Error ? err.message : 'Could not start. Reload the activity.', 'error');
  }
}

void boot();
